import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { createVehicleSchema } from "../../schemavalidations/vehicleschema";
import { CreateVehicle, Vehicle } from "../../types/vehicles";
import { FormField, Input, Select, Textarea } from "../UI/FormField";
import { SectionCard } from "../UI/SectionCard";
import Button from "../UI/Button";
import { Truck, Pencil, StickyNote } from "lucide-react";
import { useTranslation } from "react-i18next";

interface VehicleFormProps {
  vehicle?: Vehicle;
  onSubmit: (data: CreateVehicle | Partial<Vehicle>) => Promise<void>;
  loading: boolean;
  isEditMode?: boolean;
  /** Starting values for a new vehicle (ignored in edit mode). */
  initialValues?: Partial<CreateVehicle>;
}

export const VehicleForm: React.FC<VehicleFormProps> = ({
  vehicle,
  onSubmit,
  loading,
  isEditMode = false,
  initialValues,
}) => {
  const { t } = useTranslation("vehicles");
  const [readonly, setReadonly] = useState(isEditMode);

  const handleUnlockFields = () => setReadonly(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<CreateVehicle>({
    resolver: yupResolver(createVehicleSchema) as any,
    context: { isCreateMode: !isEditMode },
  });

  useEffect(() => {
    if (vehicle && isEditMode) {
      reset({
        registrationNumber: vehicle.registrationNumber,
        displayName: vehicle.displayName || "",
        markAndModel: vehicle.markAndModel || "",
        type: vehicle.type,
        additionalNotes: vehicle.additionalNotes || "",
        weightCapacity: vehicle.weightCapacity || 0,
        numberOfAxles: vehicle.numberOfAxles || 0,
      });
    } else if (!isEditMode) {
      reset({
        registrationNumber: "",
        displayName: "",
        markAndModel: "",
        type: "TRUCK",
        additionalNotes: "",
        weightCapacity: 0,
        numberOfAxles: 1,
        ...initialValues,
      });
    }
    // `initialValues` seeds a new form once; it is not a live binding.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicle, isEditMode, reset]);

  const onFormSubmit: (data: CreateVehicle) => Promise<void> = (data) => onSubmit(data);

  const Vehicle_Types = [
    { value: "TRUCK", label: t("form.vehicleTypes.TRUCK") },
    { value: "TRAILER", label: t("form.vehicleTypes.TRAILER") },
  ];

  if (isEditMode && readonly) {
    return (
      <div className="mx-auto bg-gray-50 rounded-md overflow-hidden">
        <div className="p-3 space-y-2">
          <SectionCard icon={Truck} title={t("form.readonlyView.sectionTitle")}>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 text-[13px]">
              <div className="flex gap-2">
                <dt className="w-32 text-gray-500 text-[12px]">
                  {t("form.fields.licensePlate.label")}
                </dt>
                <dd className="text-gray-900 font-medium">
                  {vehicle?.registrationNumber}
                </dd>
              </div>
              <div className="flex gap-2">
                <dt className="w-32 text-gray-500 text-[12px]">
                  {t("form.fields.displayName.label")}
                </dt>
                <dd className="text-gray-900">
                  {vehicle?.displayName || t("form.readonlyView.notSpecified")}
                </dd>
              </div>
              <div className="flex gap-2">
                <dt className="w-32 text-gray-500 text-[12px]">
                  {t("form.fields.type.label")}
                </dt>
                <dd className="text-gray-900">{vehicle?.type}</dd>
              </div>
              <div className="flex gap-2">
                <dt className="w-32 text-gray-500 text-[12px]">
                  {t("form.fields.markModel.label")}
                </dt>
                <dd className="text-gray-900">
                  {vehicle?.markAndModel ||
                    t("form.readonlyView.notSpecified")}
                </dd>
              </div>
              <div className="flex gap-2">
                <dt className="w-32 text-gray-500 text-[12px]">
                  {t("form.fields.numberOfAxles.label")}
                </dt>
                <dd className="text-gray-900">{vehicle?.numberOfAxles || 0}</dd>
              </div>
              <div className="flex gap-2">
                <dt className="w-32 text-gray-500 text-[12px]">
                  {t("form.fields.weightCapacity.label")}
                </dt>
                <dd className="text-gray-900">
                  {vehicle?.weightCapacity || 0} {t("table.weightUnit")}
                </dd>
              </div>
            </dl>
            {vehicle?.additionalNotes && (
              <div className="mt-2 pt-2 border-t border-gray-100 text-[13px]">
                <p className="text-[12px] text-gray-500 mb-0.5">
                  {t("form.fields.additionalNotes.label")}
                </p>
                <p className="text-gray-900 whitespace-pre-wrap">
                  {vehicle.additionalNotes}
                </p>
              </div>
            )}
          </SectionCard>
        </div>

        <div className="bg-white px-4 py-2.5 border-t border-gray-200 flex justify-end">
          <Button
            type="button"
            onClick={handleUnlockFields}
            variant="primary"
            icon={<Pencil className="w-3.5 h-3.5" />}
          >
            {t("actions.edit")}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onFormSubmit)}>
      <div className="p-3 space-y-2">
        <SectionCard icon={Truck} title={t("form.sectionTitle")}>
          <div className="space-y-1">
            <FormField
              layout="inline"
              size="sm"
              label={t("form.fields.licensePlate.label")}
              required
              error={errors.registrationNumber?.message}
            >
              <Input
                {...register("registrationNumber")}
                size="sm"
                error={!!errors.registrationNumber}
                placeholder={t("form.fields.licensePlate.placeholder")}
                readOnly={readonly}
              />
            </FormField>

            <FormField
              layout="inline"
              size="sm"
              label={t("form.fields.displayName.label")}
              error={errors.displayName?.message}
            >
              <Input
                {...register("displayName")}
                size="sm"
                error={!!errors.displayName}
                placeholder={t("form.fields.displayName.placeholder")}
                readOnly={readonly}
              />
            </FormField>

            <FormField
              layout="inline"
              size="sm"
              label={t("form.fields.type.label")}
              required
              error={errors.type?.message}
            >
              <Select
                {...register("type")}
                size="sm"
                options={Vehicle_Types}
                disabled={readonly}
                placeholder={t("form.fields.type.placeholder")}
              />
            </FormField>

            <FormField
              layout="inline"
              size="sm"
              label={t("form.fields.markModel.label")}
              error={errors.markAndModel?.message}
            >
              <Input
                {...register("markAndModel")}
                size="sm"
                error={!!errors.markAndModel}
                placeholder={t("form.fields.markModel.placeholder")}
                readOnly={readonly}
              />
            </FormField>

            <FormField
              layout="inline"
              size="sm"
              label={t("form.fields.weightCapacity.label")}
              error={errors.weightCapacity?.message}
            >
              <Input
                {...register("weightCapacity")}
                type="number"
                step={0.01}
                size="sm"
                error={!!errors.weightCapacity}
                placeholder={t("form.fields.weightCapacity.placeholder")}
              />
            </FormField>

            <FormField
              layout="inline"
              size="sm"
              label={t("form.fields.numberOfAxles.label")}
              error={errors.numberOfAxles?.message}
            >
              <Input
                {...register("numberOfAxles")}
                type="number"
                step={1}
                size="sm"
                error={!!errors.numberOfAxles}
                placeholder={t("form.fields.numberOfAxles.placeholder")}
                readOnly={readonly}
              />
            </FormField>
          </div>
        </SectionCard>

        <SectionCard icon={StickyNote} title={t("form.fields.additionalNotes.label")}>
          <FormField
            layout="inline"
            size="sm"
            label={t("form.fields.additionalNotes.label")}
            error={errors.additionalNotes?.message}
          >
            <Textarea
              {...register("additionalNotes")}
              size="sm"
              error={!!errors.additionalNotes}
              placeholder={t("form.fields.additionalNotes.placeholder")}
              readOnly={readonly}
              rows={3}
            />
          </FormField>
        </SectionCard>
      </div>

      <div className="flex justify-end items-center gap-3 px-4 py-2.5 border-t border-gray-200 bg-white shrink-0">
        {isEditMode && readonly && (
          <Button
            type="button"
            onClick={handleUnlockFields}
            variant="outline"
            icon={<Pencil className="w-3.5 h-3.5" />}
          >
            {t("actions.edit")}
          </Button>
        )}
        {(!isEditMode || (isEditMode && !readonly)) && (
          <Button
            type="submit"
            isLoading={loading}
            disabled={loading}
            variant="primary"
          >
            {isEditMode ? t("actions.update") : t("actions.create")}
          </Button>
        )}
      </div>
    </form>
  );
};
